// src/pages/InwardRegister.js (v3) — Inward Case Receive Note register (replaces the paper register).
// Marg purchase entries arrive automatically; staff fill in invoice date, checker, transport, L.R. and article counts.
import React, { useState, useEffect, useCallback } from 'react';
import { get, post, patch, getUser } from '../utils/api';
import { Spinner, Alert, Modal, Input, Select, PageHeader } from '../components/Shared';

const STATUS_STYLE = {
  not_logged: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  ok:         'bg-green-100 text-green-700',
  short:      'bg-red-100 text-red-700',
  excess:     'bg-amber-100 text-amber-700',
  closed:     'bg-blue-100 text-blue-700',
};
const STATUS_LABEL = { not_logged: 'To log', ok: 'Matched', short: 'Short', excess: 'Excess', closed: 'Closed' };
const TABS = [
  { key: 'not_logged', label: 'To log' },
  { key: 'open',       label: 'Short / Excess' },
  { key: 'all',        label: 'All' },
];

function ymd(d) {
  const p = n => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}
function show(d) { if (!d) return ''; const [y, m, dd] = String(d).slice(0, 10).split('-'); return dd + '-' + m + '-' + y; }
function money(v) { return v === null || v === undefined ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }); }

function StatusPill({ row }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLE[row.status] || ''}`}>
        {STATUS_LABEL[row.status] || row.status}
        {(row.status === 'short' || row.status === 'excess') ? ` ${Math.abs(row.balance)}` : ''}
      </span>
      {row.is_cancelled && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">Cancelled in Marg</span>}
      {!row.is_cancelled && Number(row.amount) === 0 && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">No amount in Marg</span>}
    </span>
  );
}

export default function InwardRegister() {
  const [tab, setTab]       = useState('not_logged');
  const [from, setFrom]     = useState(ymd(new Date()));
  const [to, setTo]         = useState(ymd(new Date()));
  const [q, setQ]           = useState('');
  const [showCancelled, setShowCancelled] = useState(false);
  const [rows, setRows]     = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]       = useState(null);
  const [options, setOptions] = useState({ checkers: [], transports: [] });
  const [logRow, setLogRow]   = useState(null);   // row being logged / edited
  const [detail, setDetail]   = useState(null);   // row opened for late receipts / close

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ from, to, status: tab });
      if (q.trim()) p.set('q', q.trim());
      if (showCancelled) p.set('include_cancelled', 'true');
      const d = await get('/api/inward?' + p.toString());
      setRows(d.rows || []);
      setSummary(d.summary || {});
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  }, [from, to, tab, q, showCancelled]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { get('/api/inward/options').then(setOptions).catch(e => setMsg({ type: 'error', text: e.message })); }, []);

  function afterSave(row, text) {
    setMsg({ type: 'success', text });
    setLogRow(null);
    if (detail && row) setDetail(row);
    load();
  }

  // A4 landscape print of the rows currently listed (same filters as the screen), one line per entry.
  function printRegister() {
    const esc = s => String(s === null || s === undefined ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const gap = r => {
      if (!r.receipt_id) return '';
      if (r.status === 'closed') return 'Closed: ' + (r.closed_note || '');
      if (r.balance < 0) return 'Short ' + (-r.balance);
      if (r.balance > 0) return 'Excess ' + r.balance;
      return '';
    };
    const body = rows.map((r, i) => '<tr>' + [
      i + 1, r.supplier_name || r.supplier_code, r.entry_no, show(r.entry_date),
      (r.supplier_bill_no && r.supplier_bill_no !== r.entry_no) ? r.supplier_bill_no : '',
      show(r.invoice_date || r.marg_invoice_date), r.amount ? money(r.amount) : '',
      r.checked_by_name, r.transport_name, r.lr_number, show(r.lr_date),
      r.receipt_id ? r.articles_in_lr : '', r.receipt_id ? r.total_received : '', gap(r),
    ].map((c, k) => '<td' + (k === 6 ? ' class="r"' : (k === 11 || k === 12) ? ' class="c"' : '') + '>' + esc(c) + '</td>').join('') + '</tr>').join('');
    const heads = ['#', 'Party name', 'Purchase entry no.', 'Entry date', 'Invoice no.', 'Invoice date', 'Invoice amount', 'Checked and verified by', 'Transport', 'L.R. no.', 'L.R. date', 'Articles in L.R.', 'Articles received', 'Short / Excess'];
    const user = (getUser() || {}).name || '';
    const html = '<!doctype html><html><head><meta charset="utf-8"><title>Inward Case Receive Note</title><style>' +
      '@page{size:A4 landscape;margin:10mm}body{font-family:Arial,Helvetica,sans-serif;font-size:9px;color:#000}' +
      'h1{font-size:15px;margin:0 0 2px}p{margin:0 0 6px;font-size:10px}table{width:100%;border-collapse:collapse}' +
      'th,td{border:1px solid #444;padding:3px 4px;vertical-align:top}th{background:#eee;text-align:left}' +
      'td.r{text-align:right}td.c{text-align:center}tr{page-break-inside:avoid}thead{display:table-header-group}' +
      '</style></head><body><h1>Inward Case Receive Note</h1><p>Entry date: ' + esc(show(from)) + ' to ' + esc(show(to)) +
      ' &nbsp;|&nbsp; ' + rows.length + ' entries &nbsp;|&nbsp; Printed ' + esc(new Date().toLocaleString('en-IN')) + (user ? ' by ' + esc(user) : '') +
      '</p><table><thead><tr>' + heads.map(h => '<th>' + h + '</th>').join('') + '</tr></thead><tbody>' + body + '</tbody></table></body></html>';
    const w = window.open('', '_blank');
    if (!w) { setMsg({ type: 'error', text: 'The print window was blocked. Allow pop-ups for this site and try again.' }); return; }
    w.document.open(); w.document.write(html); w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  }

  const counts = {
    not_logged: summary.not_logged || 0,
    open: (summary.short || 0) + (summary.excess || 0),
    all: Object.values(summary).reduce((a, b) => a + b, 0),
  };

  return (
    <div className="p-4 md:p-6">
      <PageHeader title="Inward Register" subtitle="Supplier consignments against Marg purchase entries" />
      {msg && <Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} />}

      <div className="flex flex-wrap gap-2 mb-3">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${tab === t.key ? 'bg-brand-700 text-white border-brand-700' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600'}`}>
            {t.label} <span className="opacity-80">({counts[t.key]})</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="block text-xs text-gray-500 mb-1">Entry date from</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-sm" /></div>
        <div><label className="block text-xs text-gray-500 mb-1">to</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-sm" /></div>
        <div className="flex-1 min-w-[180px]"><label className="block text-xs text-gray-500 mb-1">Search supplier, entry no, bill no, L.R.</label>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-sm" /></div>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 pb-1.5">
          <input type="checkbox" checked={showCancelled} onChange={e => setShowCancelled(e.target.checked)} className="accent-brand-700" />
          Show cancelled
        </label>
        <button onClick={printRegister} disabled={loading || rows.length === 0}
          className="mb-0.5 px-4 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
          Print (A4)
        </button>
      </div>

      {loading ? <Spinner /> : (
        <div className="overflow-x-auto bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Entry</th><th className="px-3 py-2">Party</th><th className="px-3 py-2">Supplier bill</th>
                <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Transport / L.R.</th>
                <th className="px-3 py-2 text-center">In L.R.</th><th className="px-3 py-2 text-center">Received</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">Nothing here for these filters.</td></tr>}
              {rows.map(r => (
                <tr key={r.purchase_id} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2 whitespace-nowrap"><div className="font-semibold">{r.entry_no}</div><div className="text-xs text-gray-400">{show(r.entry_date)}</div></td>
                  <td className="px-3 py-2">{r.supplier_name || r.supplier_code || '—'}</td>
                  <td className="px-3 py-2">{(r.supplier_bill_no && r.supplier_bill_no !== r.entry_no) ? r.supplier_bill_no : <span className="text-orange-600">not in Marg yet</span>}{(r.invoice_date || r.marg_invoice_date) && <div className="text-xs text-gray-400">dated {show(r.invoice_date || r.marg_invoice_date)}</div>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{money(r.amount)}</td>
                  <td className="px-3 py-2">{r.receipt_id ? <><div>{r.transport_name}</div><div className="text-xs text-gray-400">{r.lr_number} · {show(r.lr_date)}</div></> : ''}</td>
                  <td className="px-3 py-2 text-center">{r.receipt_id ? r.articles_in_lr : ''}</td>
                  <td className="px-3 py-2 text-center">{r.receipt_id ? r.total_received : ''}{r.late_received > 0 && <div className="text-xs text-gray-400">incl. {r.late_received} late</div>}</td>
                  <td className="px-3 py-2"><StatusPill row={r} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.receipt_id
                      ? <button onClick={() => setDetail(r)} className="text-xs px-2 py-1 rounded font-medium bg-blue-50 text-blue-700 hover:bg-blue-100">Details</button>
                      : <button onClick={() => setLogRow(r)} className="text-xs px-3 py-1 rounded font-semibold bg-brand-700 text-white hover:bg-brand-500">Enter details</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logRow && <LogModal row={logRow} options={options} onClose={() => setLogRow(null)}
        onSaved={(row, text) => afterSave(row, text)} />}
      {detail && <DetailModal row={detail} onClose={() => setDetail(null)}
        onChanged={(row, text) => { setDetail(row); setMsg({ type: 'success', text }); load(); }}
        onEdit={() => { setLogRow(detail); }} />}
    </div>
  );
}

function LogModal({ row, options, onClose, onSaved }) {
  const editing = !!row.receipt_id;
  const [f, setF] = useState({
    invoice_date: row.invoice_date || row.marg_invoice_date || '',
    checked_by: row.checked_by || '',
    transport_id: row.transport_id || '',
    lr_number: row.lr_number || '',
    lr_date: row.lr_date || '',
    articles_in_lr: row.articles_in_lr ?? '',
    articles_received: row.articles_received ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = k => e => setF({ ...f, [k]: e.target.value });

  const inLr = f.articles_in_lr === '' ? null : Number(f.articles_in_lr);
  const rec = f.articles_received === '' ? null : Number(f.articles_received);
  const late = editing ? Number(row.late_received || 0) : 0;
  const diff = inLr === null || rec === null ? null : rec + late - inLr;

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const body = { ...f, articles_in_lr: Number(f.articles_in_lr), articles_received: Number(f.articles_received) };
      const d = editing ? await patch('/api/inward/receipt/' + row.receipt_id, body) : await post('/api/inward/' + row.purchase_id + '/receipt', body);
      onSaved(d.row, editing ? 'Changes saved' : row.entry_no + ' logged');
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <Modal title={(editing ? 'Edit details: ' : 'Enter details: ') + row.entry_no} onClose={onClose}>
      <div className="mb-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
        <div className="font-semibold">{row.supplier_name || row.supplier_code}</div>
        <div className="text-gray-500">Entry {show(row.entry_date)} · Bill {row.supplier_bill_no || '—'} · ₹{money(row.amount)}</div>
        {row.is_cancelled && <div className="mt-1 text-red-600 font-semibold">This entry is marked cancelled in Marg.</div>}
        {(!row.supplier_bill_no || row.supplier_bill_no === row.entry_no) && <div className="mt-1 text-orange-600">Supplier bill number is not in Marg yet for this entry.</div>}
        {Number(row.amount) === 0 && <div className="mt-1 text-orange-600">Amount is 0 in Marg: the entry was probably saved before it was completed.</div>}
      </div>
      {err && <Alert type="error" message={err} onClose={() => setErr(null)} />}
      <form onSubmit={submit}>
        <Input label={(!editing && row.marg_invoice_date) ? "Invoice date (filled from Marg: please check it)" : "Invoice date (from the supplier's invoice)"} type="date" value={f.invoice_date} onChange={set('invoice_date')} required />
        <Select label="Checked and verified by" value={f.checked_by} onChange={set('checked_by')} required
          options={[{ value: '', label: 'Select…' }, ...options.checkers.map(c => ({ value: c.id, label: c.name }))]} />
        <Select label="Transport" value={f.transport_id} onChange={set('transport_id')} required
          options={[{ value: '', label: 'Select…' }, ...options.transports.map(t => ({ value: t.id, label: t.name }))]} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="L.R. number" value={f.lr_number} onChange={set('lr_number')} required />
          <Input label="L.R. date" type="date" value={f.lr_date} onChange={set('lr_date')} required />
          <Input label="Articles in L.R." type="number" min="0" value={f.articles_in_lr} onChange={set('articles_in_lr')} required />
          <Input label="Articles received" type="number" min="0" value={f.articles_received} onChange={set('articles_received')} required />
        </div>
        {diff !== null && (
          <p className={`text-sm font-semibold mb-2 ${diff === 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-amber-600'}`}>
            {diff === 0 ? 'Matches the L.R.' : diff < 0 ? `Short by ${-diff}` : `Excess by ${diff}`}
          </p>
        )}
        <div className="flex gap-3 mt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
          <button type="submit" disabled={saving} className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">{saving ? 'Saving...' : (editing ? 'Save Changes' : 'Save')}</button>
        </div>
      </form>
    </Modal>
  );
}

function DetailModal({ row, onClose, onChanged, onEdit }) {
  const [late, setLate] = useState([]);
  const [lf, setLf] = useState({ received_on: ymd(new Date()), articles: '', note: '' });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const loadLate = useCallback(() => {
    get('/api/inward/receipt/' + row.receipt_id + '/late').then(d => setLate(d.late || [])).catch(e => setErr(e.message));
  }, [row.receipt_id]);
  useEffect(() => { loadLate(); }, [loadLate, row.late_received]);

  async function run(fn, text) {
    setBusy(true); setErr(null);
    try { const d = await fn(); onChanged(d.row, text); loadLate(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  const closed = row.status === 'closed';
  const hasGap = row.balance !== 0;

  return (
    <Modal title={row.entry_no + ' — ' + (row.supplier_name || '')} onClose={onClose} width="max-w-xl">
      {err && <Alert type="error" message={err} onClose={() => setErr(null)} />}
      <div className="text-sm space-y-1 mb-3">
        <div className="flex justify-between"><span className="text-gray-500">Supplier bill</span><span>{row.supplier_bill_no || '—'} {(row.invoice_date || row.marg_invoice_date) && `(dated ${show(row.invoice_date || row.marg_invoice_date)})`}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Entry date / amount</span><span>{show(row.entry_date)} · ₹{money(row.amount)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Checked by</span><span>{row.checked_by_name}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Transport</span><span>{row.transport_name}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">L.R.</span><span>{row.lr_number} · {show(row.lr_date)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Articles in L.R. / received at entry</span><span>{row.articles_in_lr} / {row.articles_received}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Received later</span><span>{row.late_received}</span></div>
        <div className="flex justify-between font-semibold"><span>Status</span><StatusPill row={row} /></div>
      </div>

      {closed && <div className="mb-3 p-3 rounded-lg bg-blue-50 text-blue-800 text-sm">Closed: {row.closed_note}</div>}

      {late.length > 0 && (
        <div className="mb-3">
          <p className="text-xs uppercase text-gray-500 mb-1">Received later</p>
          {late.map(l => (
            <div key={l.id} className="text-sm flex justify-between border-t border-gray-100 dark:border-gray-800 py-1">
              <span>{show(l.received_on)} · {l.articles} article(s){l.note ? ' · ' + l.note : ''}</span>
              <span className="text-gray-400">{l.received_by_name}</span>
            </div>
          ))}
        </div>
      )}

      {!closed && hasGap && (
        <div className="mb-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold mb-2">Record articles received later</p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Date received" type="date" value={lf.received_on} onChange={e => setLf({ ...lf, received_on: e.target.value })} />
            <Input label="Articles" type="number" min="1" value={lf.articles} onChange={e => setLf({ ...lf, articles: e.target.value })} />
          </div>
          <Input label="Note (optional)" value={lf.note} onChange={e => setLf({ ...lf, note: e.target.value })} />
          <button disabled={busy || !lf.articles} onClick={() => run(() => post('/api/inward/receipt/' + row.receipt_id + '/late', { ...lf, articles: Number(lf.articles) }), 'Late receipt recorded')}
            className="py-2 px-4 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">Add</button>
        </div>
      )}

      {!closed && hasGap && (
        <div className="mb-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold mb-2">Clear this {row.status} without more stock</p>
          <Input label="Reason (required)" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. supplier credit note raised" />
          <button disabled={busy || !note.trim()} onClick={() => run(() => post('/api/inward/receipt/' + row.receipt_id + '/close', { note }), 'Entry closed')}
            className="py-2 px-4 bg-gray-700 text-white rounded-lg text-sm font-semibold hover:bg-gray-600 disabled:opacity-60">Close entry</button>
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={onEdit} className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">Edit details</button>
        {closed && <button disabled={busy} onClick={() => run(() => post('/api/inward/receipt/' + row.receipt_id + '/reopen', {}), 'Entry reopened')}
          className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">Reopen</button>}
        <button onClick={onClose} className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500">Done</button>
      </div>
    </Modal>
  );
}
